import { Dispatch, SetStateAction, useMemo, useState, useCallback, RefObject } from 'react';
import type { ActivityType, EmbeddedCompany, ProductKind } from 'types';
import { Button, ButtonGroup, Input, Select, useDebounce } from '@ff-it/ui';
import { productKindOptions } from 'options';
import { companyFieldProps } from 'modules/core/company/components';
import { useOptions } from 'components';
import * as style from './Filter.css';
import { Icon } from 'components/Icon';
import { clearIcon } from 'styles/style.css';

export type FilterState = {
  type: ActivityType;
  /** The one active kind. Null only for an empty permitted list: invalid configuration. */
  kind: ProductKind | null;
  search: string;
  provider: EmbeddedCompany | null;
};

/**
 * One active kind at a time. A fixed kind wins; otherwise the saved product's
 * kind is restored when permitted so SERVICE/SMART products do not reopen in
 * MEDIA results. A new selection defaults to the first permitted kind, or MEDIA
 * when kinds are omitted. An empty permitted list yields null (invalid
 * configuration, no candidates) and never the omitted-kind default.
 */
export function initialActiveKind(
  permitted: ProductKind | ProductKind[] | undefined,
  savedKind?: ProductKind,
): ProductKind | null {
  if (permitted === undefined) return savedKind ?? 'MEDIA';
  if (!Array.isArray(permitted)) return permitted;
  if (permitted.length === 0) return null;
  return savedKind && permitted.includes(savedKind) ? savedKind : permitted[0];
}

export function useFilterState(
  type: ActivityType,
  permittedKinds?: ProductKind | ProductKind[],
  savedKind?: ProductKind,
): [fitler: FilterState, setFilter: Dispatch<SetStateAction<FilterState>>] {
  return useState<FilterState>(() => ({
    type,
    kind: initialActiveKind(permittedKinds, savedKind),
    search: '',
    provider: null,
  }));
}

export function useKindOptions(kind?: ProductKind | ProductKind[]): typeof productKindOptions | null {
  return useMemo(() => {
    if (!kind) {
      return productKindOptions;
    }
    if (Array.isArray(kind)) {
      return productKindOptions.filter((o) => kind.includes(o.value));
    }
    return null;
  }, [kind]);
}

type FilterContainerProps = {
  filter: FilterState;
  setFilter: Dispatch<SetStateAction<FilterState>>;
  kindOptions: typeof productKindOptions | null;
  searchInputRef: RefObject<HTMLInputElement>;
};

const providerFieldProps = {
  placeholder: 'Filter provider',
  ...companyFieldProps,
  classNames: {
    control: () => style.providerControl,
  },
  components: {
    DropdownIndicator: null,
  },
};

export function FilterContainer({ filter, setFilter, kindOptions, searchInputRef }: FilterContainerProps) {
  const [searchInput, setSearchInput] = useState(filter.search);
  const loadOptions = useOptions<any>('core/companies/', { include_branches: true });
  const updateFilterSearch = useCallback(
    (searchValue: string) => {
      setFilter((v) => ({ ...v, search: searchValue }));
    },
    [setFilter],
  );

  const debouncedUpdateFilter = useDebounce(updateFilterSearch, 500);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);

    // If clearing search (empty value), execute immediately
    // Otherwise, use debounced function
    if (value === '') {
      debouncedUpdateFilter.cancel(); // Cancel any pending updates
      updateFilterSearch(value); // Execute immediately
    } else {
      debouncedUpdateFilter(value);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.search}>
        <Input
          name="search"
          placeholder="Search vendors, outlets, products..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          ref={searchInputRef}
        />
        {searchInput && <Icon icon="circle-xmark" className={clearIcon} onClick={() => handleSearchChange('')} />}
      </div>
      <div className={style.provider}>
        <Select
          value={filter.provider}
          loadOptions={loadOptions}
          onChange={(provider) => setFilter((v) => ({ ...v, provider }))}
          {...providerFieldProps}
        />
        {filter.provider && (
          <Icon
            icon="circle-xmark"
            className={clearIcon}
            onClick={() => setFilter((v) => ({ ...v, provider: null }))}
          />
        )}
      </div>
      {kindOptions && (
        <ButtonGroup ml="1">
          {kindOptions.map(({ value, label }) => (
            <Button
              key={value}
              variant="secondary"
              size="sm"
              active={value === filter.kind}
              onClick={() => setFilter((s) => ({ ...s, kind: value }))}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      )}
    </div>
  );
}
